import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Plus, 
  Users,
  Clock,
  Calendar,
  CheckCircle,
  XCircle,
  RefreshCw,
  Trash2,
  LogIn,
  LogOut,
  User
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter 
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

// Use relative path for API calls (works on any domain)
const API = typeof window !== 'undefined' && window.location.hostname !== 'localhost' 
  ? '/api' 
  : `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAYS_OF_WEEK = [
  { value: 1, label: 'Lunes' },
  { value: 2, label: 'Martes' },
  { value: 3, label: 'Miércoles' },
  { value: 4, label: 'Jueves' },
  { value: 5, label: 'Viernes' },
  { value: 6, label: 'Sábado' },
  { value: 7, label: 'Domingo' }
];

export default function ShiftsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [staff, setStaff] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  
  // Dialog states
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [showShiftDialog, setShowShiftDialog] = useState(false);
  
  // Form states
  const [templateForm, setTemplateForm] = useState({
    name: '',
    start_time: '08:00',
    end_time: '16:00',
    days_of_week: [1, 2, 3, 4, 5],
    roles: []
  });
  const [shiftForm, setShiftForm] = useState({
    user_id: '',
    template_id: '',
    date: new Date().toISOString().split('T')[0],
    start_time: '08:00',
    end_time: '16:00',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchShifts();
  }, [selectedDate]);

  const fetchData = async () => {
    try {
      const [templatesRes, staffRes] = await Promise.all([
        axios.get(`${API}/shift-templates`, { withCredentials: true }),
        axios.get(`${API}/staff`, { withCredentials: true })
      ]);
      setTemplates(templatesRes.data);
      setStaff(staffRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchShifts = async () => {
    try {
      // Get shifts for the week
      const startDate = new Date(selectedDate);
      startDate.setDate(startDate.getDate() - startDate.getDay() + 1);
      const endDate = new Date(startDate);
      endDate.setDate(endDate.getDate() + 6);
      
      const response = await axios.get(
        `${API}/employee-shifts?start_date=${startDate.toISOString().split('T')[0]}&end_date=${endDate.toISOString().split('T')[0]}`,
        { withCredentials: true }
      );
      setShifts(response.data);
    } catch (error) {
      console.error('Error fetching shifts:', error);
    }
  };

  const handleCreateTemplate = async () => {
    try {
      await axios.post(`${API}/shift-templates`, templateForm, { withCredentials: true });
      toast.success('Plantilla creada');
      setShowTemplateDialog(false);
      setTemplateForm({
        name: '',
        start_time: '08:00',
        end_time: '16:00',
        days_of_week: [1, 2, 3, 4, 5],
        roles: []
      });
      fetchData();
    } catch (error) {
      toast.error('Error al crear plantilla');
    }
  };

  const handleDeleteTemplate = async (id) => {
    if (!window.confirm('¿Eliminar esta plantilla?')) return;
    try {
      await axios.delete(`${API}/shift-templates/${id}`, { withCredentials: true });
      toast.success('Plantilla eliminada');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar plantilla');
    }
  };

  const handleCreateShift = async () => {
    try {
      await axios.post(`${API}/employee-shifts`, shiftForm, { withCredentials: true });
      toast.success('Turno asignado');
      setShowShiftDialog(false);
      setShiftForm({
        user_id: '',
        template_id: '',
        date: new Date().toISOString().split('T')[0],
        start_time: '08:00',
        end_time: '16:00',
        notes: ''
      });
      fetchShifts();
    } catch (error) {
      toast.error('Error al crear turno');
    }
  };

  const handleCheckIn = async (shiftId) => {
    try {
      await axios.post(`${API}/employee-shifts/${shiftId}/check-in`, {}, { withCredentials: true });
      toast.success('Check-in registrado');
      fetchShifts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error en check-in');
    }
  };

  const handleCheckOut = async (shiftId) => {
    try {
      await axios.post(`${API}/employee-shifts/${shiftId}/check-out`, {}, { withCredentials: true });
      toast.success('Check-out registrado');
      fetchShifts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error en check-out');
    }
  };

  const handleDeleteShift = async (shiftId) => {
    if (!window.confirm('¿Eliminar este turno?')) return;
    try {
      await axios.delete(`${API}/employee-shifts/${shiftId}`, { withCredentials: true });
      toast.success('Turno eliminado');
      fetchShifts();
    } catch (error) {
      toast.error('Error al eliminar turno');
    }
  };

  const applyTemplate = (template) => {
    setShiftForm({
      ...shiftForm,
      template_id: template.template_id,
      start_time: template.start_time,
      end_time: template.end_time
    });
  };

  const getStatusBadge = (shift) => {
    switch (shift.status) {
      case 'scheduled':
        return <Badge variant="outline">Programado</Badge>;
      case 'in_progress':
        return <Badge className="bg-green-500">En turno</Badge>;
      case 'completed':
        return <Badge variant="secondary">Completado</Badge>;
      case 'absent':
        return <Badge variant="destructive">Ausente</Badge>;
      default:
        return <Badge variant="outline">{shift.status}</Badge>;
    }
  };

  // Get current week dates
  const getWeekDates = () => {
    const start = new Date(selectedDate);
    start.setDate(start.getDate() - start.getDay() + 1);
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(start);
      date.setDate(date.getDate() + i);
      dates.push(date);
    }
    return dates;
  };

  const weekDates = getWeekDates();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-card border-b border-border px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-bold text-xl">Turnos</h1>
              <p className="text-sm text-muted-foreground">Gestión de horarios</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowTemplateDialog(true)}>
              <Clock className="w-4 h-4 mr-2" />
              Nueva Plantilla
            </Button>
            <Button onClick={() => setShowShiftDialog(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Asignar Turno
            </Button>
          </div>
        </div>
      </header>

      <main className="p-4 max-w-6xl mx-auto">
        <Tabs defaultValue="calendar">
          <TabsList className="mb-4">
            <TabsTrigger value="calendar" className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Calendario
            </TabsTrigger>
            <TabsTrigger value="templates" className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Plantillas ({templates.length})
            </TabsTrigger>
          </TabsList>

          {/* Calendar Tab */}
          <TabsContent value="calendar">
            {/* Week Navigation */}
            <div className="flex items-center justify-between mb-4">
              <Button 
                variant="outline" 
                onClick={() => {
                  const newDate = new Date(selectedDate);
                  newDate.setDate(newDate.getDate() - 7);
                  setSelectedDate(newDate.toISOString().split('T')[0]);
                }}
              >
                ← Semana anterior
              </Button>
              <div className="text-center">
                <p className="font-semibold">
                  {weekDates[0].toLocaleDateString('es', { day: 'numeric', month: 'short' })} - {weekDates[6].toLocaleDateString('es', { day: 'numeric', month: 'short', year: 'numeric' })}
                </p>
              </div>
              <Button 
                variant="outline"
                onClick={() => {
                  const newDate = new Date(selectedDate);
                  newDate.setDate(newDate.getDate() + 7);
                  setSelectedDate(newDate.toISOString().split('T')[0]);
                }}
              >
                Semana siguiente →
              </Button>
            </div>

            {/* Week Grid */}
            <div className="grid grid-cols-7 gap-2">
              {weekDates.map((date, idx) => {
                const dateStr = date.toISOString().split('T')[0];
                const dayShifts = shifts.filter(s => s.date === dateStr);
                const isToday = dateStr === new Date().toISOString().split('T')[0];
                
                return (
                  <Card key={idx} className={isToday ? 'ring-2 ring-primary' : ''}>
                    <CardHeader className="p-2 text-center">
                      <p className="text-xs text-muted-foreground">
                        {DAYS_OF_WEEK[idx].label}
                      </p>
                      <p className={`text-lg font-bold ${isToday ? 'text-primary' : ''}`}>
                        {date.getDate()}
                      </p>
                    </CardHeader>
                    <CardContent className="p-2">
                      {dayShifts.length === 0 ? (
                        <p className="text-xs text-center text-muted-foreground py-4">Sin turnos</p>
                      ) : (
                        <div className="space-y-1">
                          {dayShifts.map((shift) => (
                            <div 
                              key={shift.shift_id}
                              className={`p-2 rounded text-xs ${
                                shift.status === 'in_progress' 
                                  ? 'bg-green-100 dark:bg-green-900' 
                                  : shift.status === 'completed'
                                    ? 'bg-gray-100 dark:bg-gray-800'
                                    : 'bg-blue-50 dark:bg-blue-900'
                              }`}
                            >
                              <p className="font-medium truncate">{shift.user_name}</p>
                              <p className="text-muted-foreground">
                                {shift.start_time} - {shift.end_time}
                              </p>
                              {shift.status === 'scheduled' && isToday && (
                                <Button 
                                  size="sm" 
                                  className="w-full mt-1 h-6 text-xs"
                                  onClick={() => handleCheckIn(shift.shift_id)}
                                >
                                  <LogIn className="w-3 h-3 mr-1" />
                                  Entrada
                                </Button>
                              )}
                              {shift.status === 'in_progress' && (
                                <Button 
                                  size="sm" 
                                  variant="destructive"
                                  className="w-full mt-1 h-6 text-xs"
                                  onClick={() => handleCheckOut(shift.shift_id)}
                                >
                                  <LogOut className="w-3 h-3 mr-1" />
                                  Salida
                                </Button>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Today's Shifts Detail */}
            <Card className="mt-6">
              <CardHeader>
                <CardTitle>Turnos de Hoy</CardTitle>
              </CardHeader>
              <CardContent>
                {shifts.filter(s => s.date === new Date().toISOString().split('T')[0]).length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No hay turnos programados para hoy</p>
                ) : (
                  <div className="divide-y">
                    {shifts
                      .filter(s => s.date === new Date().toISOString().split('T')[0])
                      .map((shift) => (
                        <div key={shift.shift_id} className="py-3 flex justify-between items-center">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                              <User className="w-5 h-5 text-primary" />
                            </div>
                            <div>
                              <p className="font-medium">{shift.user_name}</p>
                              <p className="text-sm text-muted-foreground">
                                {shift.start_time} - {shift.end_time}
                                {shift.template_name && ` (${shift.template_name})`}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {getStatusBadge(shift)}
                            {shift.check_in && (
                              <span className="text-xs text-green-600">
                                Entrada: {shift.check_in.split('T')[1]?.slice(0,5)}
                              </span>
                            )}
                            {shift.check_out && (
                              <span className="text-xs text-red-600">
                                Salida: {shift.check_out.split('T')[1]?.slice(0,5)}
                              </span>
                            )}
                            <Button 
                              variant="ghost" 
                              size="icon"
                              className="text-red-500"
                              onClick={() => handleDeleteShift(shift.shift_id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Templates Tab */}
          <TabsContent value="templates">
            {templates.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <Clock className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No hay plantillas</h3>
                  <p className="text-muted-foreground mb-4">Crea plantillas para asignar turnos más rápido</p>
                  <Button onClick={() => setShowTemplateDialog(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Crear Plantilla
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {templates.map((template) => (
                  <Card key={template.template_id}>
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h3 className="font-semibold text-lg">{template.name}</h3>
                          <p className="text-primary font-mono">
                            {template.start_time} - {template.end_time}
                          </p>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          className="text-red-500"
                          onClick={() => handleDeleteTemplate(template.template_id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {template.days_of_week.map(day => (
                          <Badge key={day} variant="outline" className="text-xs">
                            {DAYS_OF_WEEK.find(d => d.value === day)?.label.slice(0, 3)}
                          </Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Template Dialog */}
      <Dialog open={showTemplateDialog} onOpenChange={setShowTemplateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nueva Plantilla de Turno</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nombre</Label>
              <Input
                value={templateForm.name}
                onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                placeholder="Ej: Turno Mañana"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Hora inicio</Label>
                <Input
                  type="time"
                  value={templateForm.start_time}
                  onChange={(e) => setTemplateForm({ ...templateForm, start_time: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Hora fin</Label>
                <Input
                  type="time"
                  value={templateForm.end_time}
                  onChange={(e) => setTemplateForm({ ...templateForm, end_time: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Días de la semana</Label>
              <div className="flex flex-wrap gap-2">
                {DAYS_OF_WEEK.map((day) => (
                  <Button
                    key={day.value}
                    variant={templateForm.days_of_week.includes(day.value) ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => {
                      const days = templateForm.days_of_week.includes(day.value)
                        ? templateForm.days_of_week.filter(d => d !== day.value)
                        : [...templateForm.days_of_week, day.value];
                      setTemplateForm({ ...templateForm, days_of_week: days.sort() });
                    }}
                  >
                    {day.label.slice(0, 3)}
                  </Button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTemplateDialog(false)}>Cancelar</Button>
            <Button onClick={handleCreateTemplate}>Crear</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Shift Dialog */}
      <Dialog open={showShiftDialog} onOpenChange={setShowShiftDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Asignar Turno</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Empleado</Label>
              <Select 
                value={shiftForm.user_id} 
                onValueChange={(v) => setShiftForm({ ...shiftForm, user_id: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar empleado" />
                </SelectTrigger>
                <SelectContent>
                  {staff.map((s) => (
                    <SelectItem key={s.user_id} value={s.user_id}>
                      {s.name} ({s.role})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {templates.length > 0 && (
              <div className="space-y-2">
                <Label>Usar plantilla (opcional)</Label>
                <div className="flex flex-wrap gap-2">
                  {templates.map((t) => (
                    <Button
                      key={t.template_id}
                      variant={shiftForm.template_id === t.template_id ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => applyTemplate(t)}
                    >
                      {t.name}
                    </Button>
                  ))}
                </div>
              </div>
            )}
            <div className="space-y-2">
              <Label>Fecha</Label>
              <Input
                type="date"
                value={shiftForm.date}
                onChange={(e) => setShiftForm({ ...shiftForm, date: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Hora inicio</Label>
                <Input
                  type="time"
                  value={shiftForm.start_time}
                  onChange={(e) => setShiftForm({ ...shiftForm, start_time: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Hora fin</Label>
                <Input
                  type="time"
                  value={shiftForm.end_time}
                  onChange={(e) => setShiftForm({ ...shiftForm, end_time: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notas (opcional)</Label>
              <Input
                value={shiftForm.notes}
                onChange={(e) => setShiftForm({ ...shiftForm, notes: e.target.value })}
                placeholder="Observaciones"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowShiftDialog(false)}>Cancelar</Button>
            <Button onClick={handleCreateShift}>Asignar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
